import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { motion, AnimatePresence } from 'motion/react';

export default function Toast({ 
  message, 
  type = 'info', 
  isVisible, 
  onClose, 
  autoClose = true, 
  duration = 4000 
}) {
  const [shouldShow, setShouldShow] = useState(isVisible);

  useEffect(() => {
    setShouldShow(isVisible);
  }, [isVisible]);

  useEffect(() => {
    if (shouldShow && autoClose) {
      const timer = setTimeout(() => {
        setShouldShow(false);
        setTimeout(() => onClose && onClose(), 300); // Esperar a que termine la animación
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [shouldShow, autoClose, duration, onClose]);

  const handleClose = () => {
    setShouldShow(false);
    setTimeout(() => onClose && onClose(), 300);
  };

  const getToastStyles = () => {
    switch (type) {
      case 'success':
        return {
          bgColor: 'bg-green-600',
          borderColor: 'border-green-500',
          icon: 'fa-solid fa-check-circle',
          iconColor: 'text-green-200'
        };
      case 'error':
        return {
          bgColor: 'bg-red-600',
          borderColor: 'border-red-500',
          icon: 'fa-solid fa-exclamation-circle',
          iconColor: 'text-red-200'
        };
      case 'warning':
        return {
          bgColor: 'bg-yellow-600',
          borderColor: 'border-yellow-500',
          icon: 'fa-solid fa-exclamation-triangle',
          iconColor: 'text-yellow-200'
        };
      case 'info':
      default:
        return {
          bgColor: 'bg-blue-600',
          borderColor: 'border-blue-500',
          icon: 'fa-solid fa-info-circle',
          iconColor: 'text-blue-200'
        };
    }
  };

  const styles = getToastStyles();

  return (
    <AnimatePresence>
      {shouldShow && (
        <motion.div
          initial={{ opacity: 0, y: -50, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -50, scale: 0.9 }}
          transition={{ 
            duration: 0.3,
            ease: "easeOut"
          }}
          className={`fixed top-4 right-4 z-[100] max-w-md w-full mx-4 sm:mx-0 ${styles.bgColor} ${styles.borderColor} border-l-4 rounded-lg shadow-2xl backdrop-blur-sm`}
        >
          <div className="p-4 flex items-start gap-3">
            {/* Icon */}
            <div className={`flex-shrink-0 ${styles.iconColor}`}>
              <FontAwesomeIcon 
                icon={styles.icon} 
                className="text-lg"
              />
            </div>

            {/* Content */}
            <div className="flex-grow min-w-0">
              <p className="text-white text-sm leading-relaxed break-words">
                {message}
              </p>
            </div>

            {/* Close Button */}
            <button
              onClick={handleClose}
              className="flex-shrink-0 text-white/70 hover:text-white transition-colors duration-200 p-1"
              aria-label="Cerrar notificación"
            >
              <FontAwesomeIcon 
                icon="fa-solid fa-times" 
                className="text-sm"
              />
            </button>
          </div>

          {/* Progress bar for auto-close */}
          {autoClose && (
            <motion.div
              initial={{ width: "100%" }}
              animate={{ width: "0%" }}
              transition={{ duration: duration / 1000, ease: "linear" }}
              className="h-1 bg-white/30 rounded-b-lg"
            />
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Hook personalizado para manejar múltiples toasts
export function useToast() {
  const [toasts, setToasts] = useState([]);

  const addToast = (message, type = 'info', options = {}) => {
    const id = Date.now();
    const newToast = {
      id,
      message,
      type,
      isVisible: true,
      ...options
    };

    setToasts(prev => [...prev, newToast]);

    // Auto-remove después del duration si autoClose está habilitado
    if (options.autoClose !== false) {
      const duration = options.duration || 4000;
      setTimeout(() => {
        removeToast(id);
      }, duration + 300); // +300ms para la animación de salida
    }

    return id;
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const success = (message, options = {}) => addToast(message, 'success', options);
  const error = (message, options = {}) => addToast(message, 'error', options);
  const warning = (message, options = {}) => addToast(message, 'warning', options);
  const info = (message, options = {}) => addToast(message, 'info', options);

  return {
    toasts,
    addToast,
    removeToast,
    success,
    error,
    warning,
    info
  };
}

// Componente contenedor para renderizar múltiples toasts
export function ToastContainer({ toasts, onRemove }) {
  return (
    <div className="fixed top-4 right-4 z-[100] space-y-2 pointer-events-none">
      {toasts.map((toast) => (
        <div key={toast.id} className="pointer-events-auto">
          <Toast
            message={toast.message}
            type={toast.type}
            isVisible={toast.isVisible}
            onClose={() => onRemove(toast.id)}
            autoClose={toast.autoClose}
            duration={toast.duration}
          />
        </div>
      ))}
    </div>
  );
}